<template>
  <SoDialog v-model="value" card-title="Photo Received">
    <SoForm
      ref="form"
      class="form"
      data-testid="readingform-meter"
      @submit="createNoteAndUpdateTickets()"
    >
      <div class="mb-3">
        Please enter the Case ID of the ticket where the customer has sent in
        the meter read photo/s.
      </div>
      <div class="mb-3">
        There is no need to upload the photos here for the time being.
      </div>
      <SoTextField
        v-model="caseId"
        label="Case ID"
        :rules="[rules.required]"
        class="mb-5"
      />
      <template v-if="photoUpdateNotes.length > 0">
        <h5>Photo Update Note added on:</h5>
        <v-list class="mb-5" lines="one" tag="ul">
          <v-list-item
            v-for="(note, index) in photoUpdateNotes"
            :key="index"
            tag="li"
          >
            <p class="d-flex align-center">
              <SoIcon icon="pencil" color="danger" />
              <span class="ml-2">
                Note {{ note.id }} -
                {{ daysFromTodayFormatted(note.createdDate) }}
              </span>
            </p>
          </v-list-item>
        </v-list>
      </template>

      <template v-if="photoRequestTickets.length > 0">
        <h5>Open tickets:</h5>
        <v-list class="mb-5" lines="one" tag="ul">
          <v-list-item
            v-for="(ticket, index) in photoRequestTickets"
            :key="index"
            tag="li"
          >
            <p class="d-flex align-center">
              <SoIcon icon="note" />
              <span class="mx-2">{{ ticket.name }} - {{ ticket.id }}</span>
              <SoChip :text="ticket.statusText" />
            </p>
          </v-list-item>
        </v-list>
      </template>

      <SoAlert
        v-if="addNoteMutator.isSuccess"
        ref="success-alert"
        type="success"
        title="Note added"
        class="mb-5"
      />
      <SoAlert
        v-if="addNoteMutator.isError"
        ref="failure-alert"
        type="error"
        title="Failed to add a note"
        class="mb-5"
      />
      <SoAlert
        v-if="updateTicketMutator.isSuccess && !failedTickets.length"
        ref="success-alert"
        type="success"
        title="Ticket updated"
        class="mb-5"
      />
      <SoAlert
        v-if="failedTickets.length"
        ref="failure-alert"
        type="error"
        title="Failed to update tickets:"
        class="mb-5"
        >{{ getTicketCodes(failedTickets) }}</SoAlert
      >
      <SoButton
        class="mr-2"
        title="Submit"
        :disabled="isSubmitted"
        :loading="isNotesSavingLoading"
        type="primary"
        submit
        large
      />
    </SoForm>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { daysFromTodayFormatted, formatDate } from "@/helpers/dateHelper"
  import SoDialog from "@/components/SoDialog.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IJuniferTicket } from "@/models/juniferTickets/juniferTicket.types"
  import { errorReporter } from "@/services/ErrorService"
  import rules from "@/validators/rules"

  export default defineComponent({
    components: {
      SoAlert,
      SoButton,
      SoDialog,
      SoChip,
      SoForm,
      SoIcon,
      SoTextField,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        daysFromTodayFormatted,
        formatDate,
        current: useCurrentAccountStore(),
        addNoteMutator: useCurrentAccountStore().addNote(),
        updateTicketMutator: useCurrentAccountStore().updateJuniferTicket(),
        caseId: "",
        isSubmitted: false,
        failedTickets: [] as IJuniferTicket[],
        rules,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      photoRequestTickets() {
        return this.current.juniferTickets.data.filter(
          (ticket) => ticket.isPhotoRequestTicket,
        )
      },
      photoUpdateNotes() {
        return this.current.notes.data.filter((note) => note.isPhotoUpdateNote)
      },
      isNotesSavingLoading() {
        return (
          this.addNoteMutator.isPending || this.updateTicketMutator.isPending
        )
      },
    },
    methods: {
      /*
        It takes the caseId added by the user, and updates all open tickets with a note
      */
      async createNoteAndUpdateTickets() {
        const forms = this.$refs.form as InstanceType<typeof SoForm>
        const { valid } = await forms.validate()
        if (!valid) return

        this.failedTickets = []
        await Promise.all([
          this.addNote(),
          ...this.photoRequestTickets.map((ticket) =>
            this.updateTicket(ticket),
          ),
        ])
        this.isSubmitted = true
        this.current.notes.refetch()
        this.current.juniferTickets.refetch()
      },
      /*
        A note failure is already surfaced by the addNoteMutator.isError alert,
        so it is caught here to let the ticket updates and the refetches below
        still complete.
      */
      addNote() {
        return this.addNoteMutator
          .mutateAsync({
            subject: "General Note",
            content: `Photo or video was received from the customer on Case Id ${this.caseId}`,
            summary: "PHOTO RECEIVED",
          })
          .catch((error: unknown) => {
            errorReporter.report(error)
          })
      },
      async updateTicket(ticket: IJuniferTicket) {
        try {
          await this.updateTicketMutator.mutateAsync({
            id: ticket.id,
            code: ticket.code,
            summary: ticket.summary,
            description: ticket.description,
            stepCode: "Response Received [A5]",
          })
        } catch (error) {
          this.failedTickets.push(ticket)
          errorReporter.report(error)
        }
      },
      getTicketCodes(tickets: IJuniferTicket[]) {
        return tickets.map((ticket) => ticket.code).join(", ")
      },
    },
  })
</script>
